import { useContext, useEffect, useState } from 'react';
import styled, { css, CSSProperties } from 'styled-components';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import { TableContext } from '../../contexts/TableContext';
import { DASHBOARD_NAV_WIDTH } from '../../layouts/ViewLayout';
import { DashboardContext } from '../../scenes/DashboardScene/DashboardContext';
import Alert from '../base/Alert';
import Button from '../base/Button';
import Divider from '../base/Divider';
import Icon, { IconName, StyledIcon } from '../base/Icon';
import Loading from '../base/Loading';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import { EmptyResults, StyledEmptyStateWrapper } from '../EmptyState';
import { Div, StyledUtilsProps } from '../helpers/StyledUtils';
import DraggableColumnHeader from './DraggableColumnHeader';
import TableFooter from './TableFooter';

type ColumnWidth = number | { max?: number; min?: number; type?: 'percentage' | 'px' };

const pagination_height = 44;
const selected_outline_width = 1;

// Helper functions for offset calculations
const calculate_sticky_offset = {
  rowWithinTable: (base_offset: number) => base_offset + selected_outline_width,
};

const StyledHeaderWrapper = styled.div<{
  full_page: boolean;
  nested?: boolean;
  top_offset: number;
}>(({ full_page, nested, top_offset, theme }) => {
  return css`
    position: relative;
    ${full_page &&
    !nested &&
    css`
      position: sticky;
      top: ${theme.pxToRem(top_offset || 0)};
      z-index: ${nested ? 3 : 4};
    `}
  `;
});

const StyledNestedBlock = styled.div<{
  full_page: boolean;
  top_offset: number;
  nested?: boolean;
}>(({ theme }) => {
  return css`
    position: relative;
    border-bottom: ${theme.border};
  `;
});

const StyledHeaders = styled.div<{
  nested?: boolean;
  full_page: boolean;
  widths: ColumnWidth[];
}>(({ theme, nested, full_page, widths }) => {
  return css`
    border-bottom: ${theme.border};
    background-color: ${theme.colors.surface.base.variant_surface_3};
    display: grid;
    grid-template-columns: ${createGridTemplate(widths, full_page)};
    min-width: fit-content;
    ${full_page &&
    css`
      bottom: ${theme.pxToRem(pagination_height)};
    `}

    ${nested &&
    css`
      ${StyledCell}:first-child {
        padding-right: ${theme.spacing(6)};
      }
      position: relative;
      top: unset;
      bottom: unset;
      z-index: 2;
      &:before {
        content: '';
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: 0;
        z-index: 6;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
  `;
});

const StyledCell = styled.div<{
  header: boolean;
  full_page: boolean;
  list?: boolean;
  selected?: boolean;
  draggable?: boolean;
  $is_first?: boolean;
  $horizontal_offset?: number;
  nested?: boolean;
  has_scrolled: boolean;
}>(
  ({
    theme,
    header,
    list,
    full_page,
    draggable,
    $is_first,
    nested,
    selected,
    $horizontal_offset = 0,
    has_scrolled,
  }) => css`
    height: 20px;
    display: flex;
    align-items: center;
    min-width: 0;
    ${draggable &&
    header &&
    css`
      position: relative;
      padding: 0;
      /* Add padding to height since it's now inside the absolutely positioned wrapper */
      height: calc(20px + ${theme.spacing(2)});
    `}

    ${!draggable &&
    css`
      padding: ${theme.spacing(header ? 1 : list ? 3 : 2)} ${theme.spacing(2)};
      ${full_page &&
      css`
        &:first-child {
          padding-left: ${theme.spacing(6)};
        }
        &:last-child {
          padding-right: ${theme.spacing(6)};
        }
      `}
    `}
    ${!list &&
    css`
      &:not(:last-child) {
        border-right: ${({ theme }) => theme.border};
      }
    `}
    ${$is_first &&
    full_page &&
    css`
      position: sticky;
      left: 0;
      z-index: ${header ? 5 : 3};
      background-color: ${theme.colors.surface.base.variant_surface_3};
      ${!header &&
      css`
        background-color: inherit;
      `}

      ${has_scrolled &&
      css`
        &:after {
          content: '';
          position: absolute;
          top: 0;
          bottom: -1px;
          right: 0;
          width: 2px;
          z-index: 4;
          background-color: transparent;
          box-shadow:
            2px 0 4px 0 rgba(0, 0, 0, 0.08),
            4px 0 8px 0 rgba(0, 0, 0, 0.08);
          pointer-events: none;
        }
      `}
    `}

    ${$is_first &&
    (nested || selected) &&
    full_page &&
    css`
      ${StyledCell}:first-child {
        padding-right: ${theme.spacing(6)};
      }
      position: sticky;
      left: 0;
      z-index: ${header ? 5 : 3};
      background-color: ${theme.colors.surface.base.variant_surface_3};
      ${!header &&
      css`
        background-color: inherit;
      `}
      &:before {
        content: '';
        position: absolute;
        top: 0px;
        bottom: -1px;
        left: 0;
        z-index: 6;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
    ${!$is_first &&
    header &&
    full_page &&
    $horizontal_offset > 0 &&
    css`
      transform: translateX(-${$horizontal_offset}px);
    `}
  `,
);

const StyledTable = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.surface.base.surface};
  height: 100%;
`;

const StyledField = styled.div<{ cursor?: CSSProperties['cursor'] }>(
  ({ cursor }) => css`
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: center;
    text-align: left;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    border: 0;
    background-color: transparent;
    outline: none;
    box-shadow: none;
    cursor: ${cursor || 'pointer'};

    &:disabled {
      color: unset;
      cursor: default;
    }
  `,
);

// Helper function to convert widths array to grid-template-columns
const createGridTemplate = (widths: ColumnWidth[], full_page: boolean): string => {
  return widths
    .map((width, i) => {
      // Handle undefined or null widths
      if (!width) {
        return '1fr';
      }

      if (typeof width === 'number') {
        return `${width * 100}%`;
      } else {
        let { min, max } = width;
        const type = width.type || 'px';
        const unit = type === 'percentage' ? '%' : 'px';

        if (unit === 'px') {
          if (min) {
            min = min + 16;
            if ((i === 0 || i === widths.length - 1) && full_page) {
              min = min + 16;
            }
          }
          if (max) {
            max = max + 16;
            if ((i === 0 || i === widths.length - 1) && full_page) {
              max = max + 16;
            }
          }
        }

        if (min && max) {
          return `minmax(${min}${unit}, ${max}${unit})`;
        } else if (min) {
          return `minmax(${min}${unit}, 1fr)`;
        } else if (max) {
          return `minmax(0, ${max}${unit})`;
        } else {
          return '1fr';
        }
      }
    })
    .join(' ');
};

type Pagination = {
  current_page: number;
  page_size: number;
  entity_name: string;
};

const StyledSelectableRow = styled.div<{
  selected?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  selectable?: boolean;
  full_page?: boolean;
  nested?: boolean;
  $widths: ColumnWidth[];
  top_offset?: number;
  rows_length?: number;
  pagination?: Pagination;
}>(
  ({
    theme,
    selected,
    selectable,
    highlighted,
    outdated,
    full_page,
    nested,
    $widths,
    rows_length,
    pagination,
  }) => css`
    position: relative;
    overflow: visible;
    display: grid;
    grid-template-columns: ${createGridTemplate($widths, full_page || false)};
    background-color: ${theme.colors.surface.base.surface};
    min-width: fit-content;

    ${!selected &&
    css`
      border-bottom: ${theme.border};

      // If it's the last row of a full page table that has as many rows as the page size, don't show the bottom border as it will make a double border.
      ${full_page &&
      rows_length === pagination?.page_size &&
      css`
        &:last-child {
          border-bottom: none;
        }
      `}
    `}
    &:last-child {
      // Display hack to hide the border on the last row when next to the footer
      transform: translateY(1px);
      margin-top: -1px;
    }
    ${selectable &&
    css`
      cursor: pointer;
      &:hover {
        &:not(:has(button:hover, a:hover)) {
          background-color: ${theme.colors.surface.base.hover.neutral};
        }
      }
    `}
    ${outdated &&
    !selected &&
    css`
      opacity: 0.6;
    `}
    ${highlighted &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: 2;
        border: 1px solid ${theme.colors.outline.focus.primary};
      }
    `}
    ${selected &&
    css`
      z-index: 3;
      margin-bottom: 1px;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        bottom: -1px;
        right: 0;
        left: 0;
        z-index: ${theme.zindex.fixed};
        border: 2px solid ${theme.colors.outline.focus.primary};
        pointer-events: none;
      }
    `}
  `,
);

const StyledLoadingOverlay = styled.div<{ full_page: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.surface.base.surface};
  opacity: 0.8;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const StyledLoadPromptWrapper = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  z-index: 1;
  background-color: var(--color-background);
`;

const StyledTableBar = styled(Div)<{
  is_loading?: boolean;
  sticky?: boolean;
  primary?: boolean;
  $clickable?: boolean;
  $top?: boolean;
  $full_page?: boolean;
}>(
  ({ theme, is_loading, sticky, primary, $clickable, $top, $full_page }) => css`
    ${$top
      ? css`
          border-bottom: ${theme.border};
        `
      : css`
          border-top: ${theme.border};
        `}

    ${primary &&
    css`
      ${Text} {
        color: ${theme.colors.on.neutral.primary};
      }
      ${StyledIcon} {
        color: ${theme.colors.on.neutral.primary};
      }
    `}

    ${$clickable &&
    css`
      cursor: pointer;
      &:hover {
        background-color: ${theme.colors.surface.base.hover.neutral};
      }
    `}

    ${sticky &&
    !$top &&
    css`
      position: sticky;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 5;
      isolation: isolate;
    `}

    ${sticky &&
    $top &&
    css`
      position: sticky;
      left: 0;
      z-index: 3;
      ${$full_page &&
      css`
        width: calc(100vw - ${DASHBOARD_NAV_WIDTH}px);
      `}
    `}

    ${is_loading
      ? css`
          background: repeating-linear-gradient(
            45deg,
            ${theme.colors.surface.base.variant_surface_2},
            ${theme.colors.surface.base.variant_surface_2} 16px,
            ${theme.colors.surface.base.variant_surface_3} 16px,
            ${theme.colors.surface.base.variant_surface_3} 32px
          );
        `
      : css`
          background: ${theme.colors.surface.base.variant_surface_3};
        `}
  `,
);

export const StyledLoadProgressBar = styled(Div)<{ value: number }>(
  ({ theme, value }) => css`
    height: 2px;
    width: 100%;
    border-radius: ${theme.radius.large};
    background-color: ${theme.colors.surface.container.neutral};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      width: ${value}%;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background: linear-gradient(90deg, rgba(0, 68, 204, 0.5) 0%, #04c 100%);
      border-radius: ${theme.radius.large};
      transition: width 0.25s linear;
    }
  `,
);

export const TableBar: React.FC<{
  is_loading?: boolean;
  load_progress?: number;
  sticky?: boolean;
  children?: React.ReactNode;
  primary?: boolean;
  onClick?: () => void;
  top?: boolean;
  full_page?: boolean;
}> = ({ is_loading, load_progress, sticky, children, primary, onClick, top, full_page }) => {
  return (
    <StyledTableBar
      is_loading={is_loading}
      sticky={sticky}
      primary={primary}
      $clickable={!!onClick}
      $top={top}
      $full_page={full_page}
      onClick={onClick}>
      {children}
      {(is_loading || load_progress !== undefined) && (
        <StyledLoadProgressBar value={load_progress ?? 0} />
      )}
    </StyledTableBar>
  );
};

type CustomField = { element: JSX.Element; clickable_area: boolean };

type Field = string | number | boolean | JSX.Element | CustomField | null;

interface Row {
  id: string | number;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  fields?: Field[];
  widths?: ColumnWidth[];
  element?: React.ReactNode;
  has_error?: boolean;
  expendable?: boolean;
}

interface NestedTable {
  headers: Header[];
  rows: Row[];
}

type Header = string | JSX.Element;

type onRowSelected = (row_id: string | undefined) => void;

interface Props {
  pagination?: Pagination;
  list?: boolean;
  fill?: boolean;
  empty_results_label?: string;
  raised?: boolean;
  footer?: boolean;
  headers: Header[];
  top_offset?: number;
  rows: (Row | NestedTable)[];
  widths: ColumnWidth[];
  current_count?: number;
  alert_top?: string;
  bottom_element?: React.ReactNode;
  sticky_bottom_element?: React.ReactNode;
  loading?: boolean;
  loading_title?: string;
  loading_message?: string;
  total_count?: number | null;
  draggable_columns?: boolean;
  draggable_column_indices?: number[];
  onNextPage?(): void;
  onPreviousPage?(): void;
  onRowSelected?: onRowSelected;
  onColumnReorder?(active_index: number, over_index: number): void;
  onColumnResize?(column_index: number, new_stored_width: number): void;
  table_body_h?: StyledUtilsProps['h'];
  table_body_style?: React.CSSProperties;
  has_new_results?: boolean;
  onRefresh?: () => void;
}

const TableField: React.FC<{ field: Field }> = ({ field }) => {
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    return (
      <StyledField style={{ overflow: 'hidden' }}>
        <Text ellipsis size="s">
          {field}
        </Text>
      </StyledField>
    );
  }

  if (field === null || field === undefined) {
    return (
      <StyledField>
        <Text muted size="s">
          –
        </Text>
      </StyledField>
    );
  }

  if (typeof field === 'object' && (field as any).element) {
    return <StyledField>{(field as CustomField).element}</StyledField>;
  }

  return <StyledField>{field as any}</StyledField>;
};

const TableRow: React.FC<{
  row: Row;
  widths: ColumnWidth[];
  onRowSelected?: onRowSelected;
  nested: boolean;
  list?: boolean;
  full_page: boolean;
  top_offset?: number;
  rows_length?: number;
  pagination?: Pagination;
}> = ({
  row,
  full_page,
  widths,
  onRowSelected,
  nested,
  list,
  top_offset,
  rows_length,
  pagination,
}) => {
  const selectable = row.selectable === false ? false : !!onRowSelected;
  let icon: IconName = row.highlighted ? 'expand_more' : 'chevron_right';
  const table_context = useContext(TableContext);
  const has_scrolled = table_context?.has_scrolled || false;

  if (row.outdated) {
    icon = 'visibility_off';
  }

  return (
    <StyledSelectableRow
      rows_length={rows_length}
      pagination={pagination}
      key={row.id}
      selectable={selectable}
      highlighted={row.highlighted}
      outdated={!nested && row.outdated}
      selected={row.selected}
      nested={nested}
      $widths={widths}
      top_offset={top_offset}
      full_page={full_page}>
      {row.element
        ? row.element
        : row.fields?.map((field, i) => (
            <StyledCell
              key={i}
              list={list}
              header={false}
              selected={row.selected}
              full_page={full_page}
              nested={nested}
              $is_first={i === 0}
              has_scrolled={has_scrolled}
              onClick={() => selectable && !!onRowSelected && onRowSelected(row.id as string)}>
              <Div h={{ px: 20 }}>
                {onRowSelected &&
                  i === 0 &&
                  !nested &&
                  (row.expendable ? (
                    row.has_error ? (
                      <Div m={{ l: -1.5, y: -1.5, r: 1.5 }} p={1.5}>
                        <Icon
                          muted={row.outdated}
                          danger={!row.outdated}
                          icon={row.outdated ? 'visibility_off' : 'error'}
                        />
                      </Div>
                    ) : (
                      <Tooltip
                        disabled={!row.outdated}
                        side="bottom"
                        tooltip="This row no longer matches your current filter."
                        offset={[-6, 6]}>
                        <Button
                          m={{ l: -1, r: 1.5 }}
                          ghost
                          small
                          icon={icon}
                          onClick={(e) => {
                            e.stopPropagation();
                            row.highlighted
                              ? onRowSelected(undefined)
                              : onRowSelected(row.id as string);
                          }}
                        />
                      </Tooltip>
                    )
                  ) : null)}
                {onRowSelected && i === 0 && nested && (
                  <Icon icon="subdirectory" small right={-0.5} left={2} />
                )}
              </Div>
              <TableField field={field} />
            </StyledCell>
          ))}
    </StyledSelectableRow>
  );
};

const Table: React.FC<Props> = ({
  headers,
  rows,
  list,
  widths,
  onRowSelected,
  current_count,
  onNextPage,
  onPreviousPage,
  pagination,
  fill,
  empty_results_label,
  footer = true,
  top_offset = 0,
  alert_top,
  bottom_element,
  sticky_bottom_element,
  loading,
  loading_title,
  loading_message,
  total_count,
  draggable_columns = false,
  draggable_column_indices = [],
  onColumnReorder,
  onColumnResize,
  table_body_h,
  table_body_style,
  onRefresh,
  has_new_results,
}) => {
  const [cached_rows, setCachedRows] = useState(rows);
  const [cached_count, setCachedCount] = useState(current_count);
  const [active_drag_id, setActiveDragId] = useState<string | null>(null);
  const [resize_width_overrides, setResizeWidthOverrides] = useState<Map<number, number>>(
    new Map(),
  );
  const [is_any_resizing, setIsAnyResizing] = useState(false);
  const { is_degraded_banner_active } = useContext(DashboardContext);
  const table_context = useContext(TableContext);
  const has_scrolled = table_context?.has_scrolled || false;

  // Compute effective widths during resize
  const effective_widths = (() => {
    if (resize_width_overrides.size === 0) return widths;
    return widths.map((w, i) => {
      const override = resize_width_overrides.get(i);
      if (override !== undefined) {
        return { min: override, max: override };
      }
      return w;
    });
  })();

  const handleResizeStart = (column_index: number) => {
    setIsAnyResizing(true);
  };

  const handleResize = (column_index: number, new_width: number) => {
    setResizeWidthOverrides((prev) => {
      const next = new Map(prev);
      next.set(column_index, new_width);
      return next;
    });
  };

  const handleResizeEnd = (column_index: number, new_width: number) => {
    setResizeWidthOverrides(new Map());
    setIsAnyResizing(false);
    onColumnResize?.(column_index, new_width);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    if (!loading) {
      setCachedRows(rows);
    } else {
      setCachedRows([]);
    }
  }, [rows, loading]);

  useEffect(() => {
    if (!loading) {
      setCachedCount(current_count);
    }
  }, [current_count, loading]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (over && active.id !== over.id && onColumnReorder) {
      const active_index = headers.findIndex((_, index) => `header-${index}` === active.id);
      const over_index = headers.findIndex((_, index) => `header-${index}` === over.id);

      // Prevent reordering if either column is not in the draggable indices
      const is_active_draggable = draggable_column_indices.includes(active_index);
      const is_over_draggable = draggable_column_indices.includes(over_index);

      if (active_index !== -1 && over_index !== -1 && is_active_draggable && is_over_draggable) {
        onColumnReorder(active_index, over_index);
      }
    }
  };

  const renderHeaders = () => {
    const is_column_draggable = (index: number) => {
      return draggable_columns && draggable_column_indices.includes(index);
    };

    return headers.map((header, i) => {
      // Extract configured width for draggable columns
      const width_config = widths[i];
      let configured_width: number | undefined;
      let stored_width: number | undefined;
      if (typeof width_config === 'object' && width_config.min === width_config.max) {
        // Fixed width column - stored_width is the raw value (before padding)
        stored_width = width_config.min;
        configured_width = width_config.min;
        if (configured_width) {
          // Add padding that createGridTemplate adds (16px base + 16px if first/last on full_page)
          configured_width = configured_width + 16;
          if ((i === 0 || i === headers.length - 1) && (fill || false)) {
            configured_width = configured_width + 16;
          }
        }
      }

      // A column is resizable if it's draggable and has a fixed stored width
      const is_resizable = is_column_draggable(i) && stored_width !== undefined;

      return (
        <StyledCell
          key={i}
          header
          full_page={fill || false}
          list={list}
          nested={false}
          draggable={is_column_draggable(i)}
          $is_first={i === 0}
          has_scrolled={has_scrolled}>
          {is_column_draggable(i) ? (
            <DraggableColumnHeader
              id={`header-${i}`}
              full_page={fill || false}
              is_first={i === 0}
              is_last={i === headers.length - 1}
              configured_width={configured_width}
              resizable={is_resizable}
              column_index={i}
              stored_width={stored_width}
              is_any_resizing={is_any_resizing}
              onResizeStart={handleResizeStart}
              onResize={handleResize}
              onResizeEnd={handleResizeEnd}>
              {typeof header === 'string' ? (
                <StyledField>
                  <Text semi muted size="xs" as="span" text_wrap={false}>
                    {header}
                  </Text>
                </StyledField>
              ) : (
                <StyledField>{header}</StyledField>
              )}
            </DraggableColumnHeader>
          ) : typeof header === 'string' ? (
            <StyledField cursor="default">
              <Text semi muted size="xs" as="span" text_wrap={false}>
                {header}
              </Text>
            </StyledField>
          ) : (
            <StyledField>{header}</StyledField>
          )}
        </StyledCell>
      );
    });
  };

  const table_width = `calc(100vw - ${DASHBOARD_NAV_WIDTH}px)`;

  const sortable_items = draggable_columns
    ? draggable_column_indices.map((i) => `header-${i}`)
    : [];

  return (
    <StyledTable>
      {rows.length > 0 && headers?.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <StyledHeaderWrapper full_page={fill || false} top_offset={top_offset}>
            <StyledHeaders full_page={fill || false} widths={effective_widths}>
              <SortableContext items={sortable_items} strategy={horizontalListSortingStrategy}>
                {renderHeaders()}
              </SortableContext>
            </StyledHeaders>
          </StyledHeaderWrapper>
          <DragOverlay>
            {active_drag_id
              ? (() => {
                  const active_header_index = headers.findIndex(
                    (_, index) => `header-${index}` === active_drag_id,
                  );
                  const active_header = headers[active_header_index];

                  // Get configured width for the active column
                  const width_config = widths[active_header_index];
                  let configured_width: number | undefined;
                  if (typeof width_config === 'object' && width_config.min === width_config.max) {
                    configured_width = width_config.min;
                    if (configured_width) {
                      configured_width = configured_width + 16;
                      if (
                        (active_header_index === 0 || active_header_index === headers.length - 1) &&
                        (fill || false)
                      ) {
                        configured_width = configured_width + 16;
                      }
                    }
                  }

                  return (
                    <StyledCell
                      header
                      full_page={fill || false}
                      list={list}
                      nested={false}
                      draggable
                      $is_first={active_header_index === 0}
                      style={{
                        width: configured_width ? `${configured_width}px` : 'auto',
                        cursor: 'grabbing',
                        opacity: 0.9,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      }}>
                      <DraggableColumnHeader
                        id={`overlay-${active_drag_id}`}
                        disabled
                        full_page={fill || false}
                        is_first={active_header_index === 0}
                        is_last={active_header_index === headers.length - 1}
                        configured_width={configured_width}>
                        {typeof active_header === 'string' ? (
                          <StyledField>
                            <Text semi muted size="xs" as="span" text_wrap={false}>
                              {active_header}
                            </Text>
                          </StyledField>
                        ) : (
                          <StyledField>{active_header}</StyledField>
                        )}
                      </DraggableColumnHeader>
                    </StyledCell>
                  );
                })()
              : null}
          </DragOverlay>
        </DndContext>
      )}
      {alert_top && (
        <>
          <Alert warning no_border>
            {alert_top}
          </Alert>
          <Divider />
        </>
      )}
      <Div
        flex={{ direction: 'column', grow: true }}
        h={table_body_h}
        style={{ minWidth: 'fit-content', ...table_body_style }}>
        {has_new_results && onRefresh && (
          <TableBar primary top sticky full_page={fill || false} onClick={() => onRefresh()}>
            <Div flex={{ align: 'center', justify: 'center', gap: 1 }} p={{ x: 6, y: 1 }}>
              <Icon icon="autorenew" small />
              <Text semi size="s">
                Load newest...
              </Text>
            </Div>
          </TableBar>
        )}
        {loading ? (
          cached_rows.length !== 0 ? (
            <StyledLoadPromptWrapper style={{ width: table_width }}>
              <StyledLoadingOverlay full_page={fill || false}>
                <Loading />
              </StyledLoadingOverlay>
            </StyledLoadPromptWrapper>
          ) : (
            <Div p={{ y: 12 }} flex={{ direction: 'column', grow: true, justify: 'center' }}>
              <Loading loading_title={loading_title} loading_message={loading_message} />
            </Div>
          )
        ) : null}
        {!loading && rows.length === 0 && (
          <>
            {fill ? (
              <StyledEmptyStateWrapper>
                <EmptyResults
                  label={
                    empty_results_label ||
                    `There is no ${pagination?.entity_name || 'data'} to show`
                  }
                />
              </StyledEmptyStateWrapper>
            ) : (
              <EmptyResults
                label={
                  empty_results_label || `There is no ${pagination?.entity_name || 'data'} to show`
                }
              />
            )}
          </>
        )}
        {cached_rows.map((row) =>
          'headers' in row ? (
            <StyledNestedBlock
              key={'nested'}
              full_page={fill || false}
              top_offset={top_offset + (is_degraded_banner_active ? 32 : 0)}
              nested>
              <StyledHeaderWrapper full_page={fill || false} nested top_offset={0}>
                <StyledHeaders nested full_page={fill || false} widths={effective_widths}>
                  {row.headers.map((header, i) => (
                    <StyledCell
                      key={i}
                      header
                      nested
                      full_page={fill || false}
                      list={list}
                      $is_first={i === 0}>
                      {typeof header === 'string' ? (
                        <StyledField as="div">
                          <Text semi muted size="xs" as="span" text_wrap={false}>
                            {header}
                          </Text>
                        </StyledField>
                      ) : (
                        <StyledField as="div">{header}</StyledField>
                      )}
                    </StyledCell>
                  ))}
                </StyledHeaders>
              </StyledHeaderWrapper>
              {row.rows.map((row) => (
                <TableRow
                  nested
                  list={list}
                  key={row.id}
                  row={row}
                  widths={row.widths ?? effective_widths}
                  onRowSelected={onRowSelected}
                  full_page={fill || false}
                  rows_length={cached_rows.length}
                  pagination={pagination}
                />
              ))}
            </StyledNestedBlock>
          ) : (
            <TableRow
              nested={false}
              list={list}
              key={row.id}
              row={row}
              widths={row.widths ?? effective_widths}
              onRowSelected={onRowSelected}
              full_page={fill || false}
              top_offset={
                calculate_sticky_offset.rowWithinTable(top_offset) +
                (is_degraded_banner_active ? 32 : 0)
              }
              rows_length={cached_rows.length}
              pagination={pagination}
            />
          ),
        )}
        {sticky_bottom_element
          ? sticky_bottom_element
          : bottom_element && <StyledLoadPromptWrapper>{bottom_element}</StyledLoadPromptWrapper>}
      </Div>
      {/* Viewing 1–5 of 5 sources */}
      {footer && ((cached_count && cached_count >= 0) || onPreviousPage || onNextPage) && (
        <TableFooter
          fill={fill}
          current_count={cached_count}
          total_count={total_count}
          loading={loading}
          pagination={pagination}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
        />
      )}
    </StyledTable>
  );
};

export default Table;
